/**
 * PanelSplitView Component
 *
 * A two-column layout with a collapsible sidebar panel on the left
 * and main content area on the right. Includes an overlay layer for
 * flyout menus and selection hints that extend beyond the sidebar.
 *
 * Structure:
 * - Container (flex row, position: relative)
 *   - Panel (flex row)
 *     - PanelContent (dark area, 384px expanded / 0px collapsed)
 *       - CloseButton (mobile)
 *       - PanelPadding (scrollable content)
 *     - ToggleStrip (24px, always visible)
 *       - CollapseButton (48px, centered on left edge)
 *   - Content (main area, flex: 1)
 *   - OverlayLayer (absolute, pointer-events: none)
 *     - Flyouts render here with pointer-events: auto
 *
 * The toggle button straddles the boundary between panelContent
 * and toggleStrip using transform: translateX(-50%).
 *
 * @example
 * ```tsx
 * <PanelSplitView
 *   variant="dark"
 *   panel={<SidebarContent />}
 *   onCollapse={(collapsed) => console.log('collapsed:', collapsed)}
 * >
 *   <MainContent />
 * </PanelSplitView>
 * ```
 *
 * @example Using the overlay for flyouts
 * ```tsx
 * // In a component inside the sidebar
 * const { showFlyout } = usePanelOverlay();
 *
 * const handleItemClick = (e: React.MouseEvent) => {
 *   const rect = e.currentTarget.getBoundingClientRect();
 *   showFlyout({
 *     id: 'item-details',
 *     content: <ItemDetails />,
 *     anchorY: rect.top + rect.height / 2,
 *   });
 * };
 * ```
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { ChevronLeft, X } from "lucide-react";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { cx } from "@/utils/cx";
import type { PanelSplitViewProps } from "./types";
import {
  PanelOverlayProvider,
  usePanelOverlay,
  type FlyoutConfig,
} from "./PanelOverlayContext";
import styles from "./PanelSplitView.module.css";

/**
 * Internal component that renders flyouts in the overlay layer
 */
const OverlayContent: React.FC<{
  containerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ containerRef }) => {
  const { flyouts, isCollapsed } = usePanelOverlay();

  // Don't render any flyouts when panel is collapsed
  if (isCollapsed || flyouts.size === 0) return null;

  return (
    <>
      {Array.from(flyouts.values()).map((flyout) => (
        <FlyoutRenderer
          key={flyout.id}
          flyout={flyout}
          containerRef={containerRef}
        />
      ))}
    </>
  );
};

/**
 * Renders a single flyout with proper positioning
 */
const FlyoutRenderer: React.FC<{
  flyout: FlyoutConfig;
  containerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ flyout, containerRef }) => {
  const { hideFlyout } = usePanelOverlay();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Calculate position relative to container
  const getPosition = useCallback(() => {
    if (!containerRef.current) return { top: flyout.anchorY };
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      top: flyout.anchorY - containerRect.top,
    };
  }, [flyout.anchorY, containerRef]);

  const position = getPosition();

  // Trigger visibility animation after mount
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setIsVisible(true);
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  // Handle click outside to close (skip for persistent flyouts)
  useEffect(() => {
    // Persistent flyouts don't close on click-outside or Escape
    if (flyout.persistent) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(e.target as Node)) {
        hideFlyout(flyout.id);
      }
    };

    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        hideFlyout(flyout.id);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [flyout.id, flyout.persistent, hideFlyout]);

  const flyoutClasses = cx(
    styles.flyout,
    isVisible && styles.visible,
    flyout.transparent && styles.transparent,
  );

  return (
    <div
      ref={flyoutRef}
      className={flyoutClasses}
      style={{
        top: position.top,
        left: flyout.offsetX ?? 0,
        transform: "translateY(-50%)",
      }}
      role="dialog"
      aria-modal="false"
      data-interactive="true"
    >
      {flyout.showArrow !== false && !flyout.transparent && (
        <div className={styles.flyoutArrow} />
      )}
      {flyout.content}
    </div>
  );
};

/**
 * Internal component containing the actual PanelSplitView structure
 */
const PanelSplitViewInner: React.FC<PanelSplitViewProps> = ({
  variant = "dark",
  panel,
  children,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onCollapse,
  onClose,
  className = "",
  "data-testid": testId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { hideAllFlyouts, setCollapsed: setContextCollapsed } =
    usePanelOverlay();

  // Use internal state if not controlled
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  // Determine if controlled or uncontrolled
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

  // Sync collapsed state with context on mount and when controlled prop changes
  useEffect(() => {
    setContextCollapsed(collapsed);
  }, [collapsed, setContextCollapsed]);

  // Handle toggle
  const handleToggle = useCallback(() => {
    const newCollapsed = !collapsed;

    // Set collapsed state in context FIRST (synchronously) so flyout consumers
    // see the correct state before any re-renders can re-show flyouts
    setContextCollapsed(newCollapsed);

    // Hide all flyouts when collapsing
    if (newCollapsed) {
      hideAllFlyouts();
    }

    if (!isControlled) {
      setInternalCollapsed(newCollapsed);
    }

    onCollapse?.(newCollapsed);
  }, [
    collapsed,
    isControlled,
    onCollapse,
    hideAllFlyouts,
    setContextCollapsed,
  ]);

  // Build class names
  const containerClasses = cx(styles.container, className);

  const panelClasses = cx(styles.panel, collapsed && styles.panelCollapsed);

  const panelContentClasses = cx(
    styles.panelContent,
    styles[variant],
    collapsed && styles.collapsed,
  );

  const toggleStripClasses = cx(
    styles.toggleStrip,
    collapsed && styles.collapsed,
  );

  const buttonClasses = cx(styles.button, collapsed && styles.collapsed);

  const closeButtonClasses = cx(
    styles.closeButton,
    !onClose && styles.mobileOnlyClose,
  );

  const overlayLayerClasses = cx(
    styles.overlayLayer,
    collapsed && styles.collapsed,
  );

  return (
    <div ref={containerRef} className={containerClasses} data-testid={testId}>
      {/* Panel wrapper containing panelContent + toggleStrip */}
      <div className={panelClasses}>
        {/* Dark panel content area */}
        <div className={panelContentClasses}>
          {/* Close button - always available on mobile, uses onClose if provided */}
          {!collapsed && (
            <div className={closeButtonClasses}>
              <IconButton
                icon={<X size={20} aria-hidden />}
                variant="close"
                size="md"
                onClick={onClose ?? handleToggle}
                aria-label="Lukk sidepanel"
                data-testid="sidebar-close-btn"
              />
            </div>
          )}
          <div className={styles.panelPadding}>{panel}</div>
        </div>

        {/* Toggle strip - always 24px wide */}
        <div
          className={toggleStripClasses}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle-strip"
        >
          {/* Collapse button - positioned directly, no wrapper */}
          <IconButton
            icon={<ChevronLeft size={24} aria-hidden={true} />}
            variant="default"
            className={buttonClasses}
            onClick={(e) => {
              e.stopPropagation(); // Prevent double-firing with strip click
              handleToggle();
            }}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-toggle-btn"
          />
        </div>
      </div>

      {/* Main content area */}
      <div className={styles.content}>{children}</div>

      {/* Overlay layer for flyouts and selection hints */}
      <div
        className={overlayLayerClasses}
        aria-hidden="true"
        data-testid="panel-overlay-layer"
      >
        <OverlayContent containerRef={containerRef} />
      </div>
    </div>
  );
};

/**
 * PanelSplitView Component
 *
 * Wraps the inner component with PanelOverlayProvider to enable
 * flyout functionality throughout the component tree.
 */
export const PanelSplitView: React.FC<PanelSplitViewProps> = (props) => {
  return (
    <PanelOverlayProvider>
      <PanelSplitViewInner {...props} />
    </PanelOverlayProvider>
  );
};

PanelSplitView.displayName = "PanelSplitView";
